import { dbg } from '../debugOverlay.js';
import type { ApiClient } from './apiClient.js';

/**
 * Server-brokered Google account LINKING for mobile (Android), the link analogue of
 * {@link file://./mobileSignIn.ts}. The deep-link handoff of the OAuth code back into the app is
 * unreliable on Android, so the app doesn't try to receive it: we start a server session, open the
 * consent URL, and POLL until the server reports the account linked (the browser callback page
 * deposits the code server-side). Both start and poll are authenticated — the session is bound to the
 * signed-in user — so, unlike sign-in, nothing custom-token is involved; the caller just refreshes.
 */
export type BrokeredLinkApi = Pick<
  ApiClient,
  'startLinkBroker' | 'startSlackLinkBroker' | 'startTodoistLinkBroker' | 'pollLinkBroker'
>;

export interface BrokeredLinkDeps {
  openUrl: (url: string) => Promise<void>;
  scopes?: string[];
  pollIntervalMs?: number;
  timeoutMs?: number;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  /** Subscribe to "app returned to the foreground" events; returns an unsubscribe fn. Injectable for
   *  tests. Defaults to the DOM `visibilitychange` listener. */
  onForeground?: (cb: () => void) => () => void;
}

/** Default foreground signal: fire `cb` whenever the document becomes visible again (the user
 *  switching back from the consent browser). No-ops off the DOM (e.g. node test env). */
function domForeground(cb: () => void): () => void {
  if (typeof document === 'undefined') return () => {};
  const handler = () => {
    if (document.visibilityState === 'visible') cb();
  };
  document.addEventListener('visibilitychange', handler);
  return () => document.removeEventListener('visibilitychange', handler);
}

/** Open the consent URL, then poll the broker session to completion. Shared by Google + Slack + Todoist. */
async function runBrokeredLink(
  sessionStart: Promise<{ authUrl: string; sessionId: string }>,
  poll: (sessionId: string) => Promise<{ status: 'pending' | 'done' | 'error' | 'unknown'; accountId?: string; error?: string }>,
  deps: BrokeredLinkDeps,
): Promise<{ accountId?: string }> {
  const interval = deps.pollIntervalMs ?? 1000;
  const timeout = deps.timeoutMs ?? 180_000;
  const now = deps.now ?? (() => Date.now());
  const sleep = deps.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  const onForeground = deps.onForeground ?? domForeground;

  dbg('brokered link: starting session');
  const { authUrl, sessionId } = await sessionStart;

  dbg('opening browser for consent');
  await deps.openUrl(authUrl);

  // Re-armable "app is foregrounded again" signal. We race it against the interval sleep so the moment
  // the user returns from the consent browser we poll IMMEDIATELY instead of waiting out the interval.
  // Android throttles/suspends background timers, so by the time the user is back the account is
  // usually already linked server-side — this is what makes the connect flip feel instant.
  let wake!: () => void;
  let foregrounded = new Promise<void>((r) => { wake = r; });
  const detach = onForeground(() => {
    wake();
    foregrounded = new Promise<void>((r) => { wake = r; });
  });

  try {
    const deadline = now() + timeout;
    let polls = 0;
    while (now() < deadline) {
      await Promise.race([sleep(interval), foregrounded]);
      polls += 1;
      try {
        const res = await poll(sessionId);
        if (res.status === 'done') {
          dbg(`brokered link done after ${polls} polls`);
          return { accountId: res.accountId };
        }
        if (res.status === 'error') throw new Error(res.error || 'Linking failed on the server.');
      } catch (e) {
        // A poll network blip shouldn't abort the flow; a server-reported 'error' (rethrown above) should.
        if (e instanceof Error && /failed on the server/i.test(e.message)) throw e;
        dbg(`brokered link poll ${polls} error: ${String(e)}`);
      }
    }
    throw new Error('Connecting timed out — please try again.');
  } finally {
    detach();
  }
}

/** Server-brokered Google account link (Android). Resolves with the linked accountId. */
export async function runBrokeredGoogleLink(api: BrokeredLinkApi, deps: BrokeredLinkDeps): Promise<{ accountId?: string }> {
  return runBrokeredLink(api.startLinkBroker(deps.scopes ?? []), (s) => api.pollLinkBroker(s), deps);
}

/** Server-brokered Slack workspace link (Android) — same deposit/poll path as Google. */
export async function runBrokeredSlackLink(api: BrokeredLinkApi, deps: BrokeredLinkDeps): Promise<{ accountId?: string }> {
  return runBrokeredLink(api.startSlackLinkBroker(), (s) => api.pollLinkBroker(s), deps);
}

/** Server-brokered Todoist account link (Android) — same deposit/poll path as Google. */
export async function runBrokeredTodoistLink(api: BrokeredLinkApi, deps: BrokeredLinkDeps): Promise<{ accountId?: string }> {
  return runBrokeredLink(api.startTodoistLinkBroker(), (s) => api.pollLinkBroker(s), deps);
}
